import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Lightbulb, Send, Trash2 } from 'lucide-react';
import { Seed } from '../../types/idea-machine';
import { ideasService } from '../../services/ideas';
import { useAuth } from '../../context/AuthContext';

interface QuickSparkDialogProps {
  seed: Seed | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function QuickSparkDialog({ seed, open, onOpenChange, onSuccess }: QuickSparkDialogProps) {
  const { userEmail } = useAuth();
  const [ideas, setIdeas] = useState<string[]>(['']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);

  // Reset state when dialog opens with a new seed
  useEffect(() => {
    if (open && seed) {
      setIdeas(['']);
      setStartTime(new Date());
    }
  }, [open, seed]);

  const handleIdeaChange = (index: number, value: string) => {
    const newIdeas = [...ideas];
    newIdeas[index] = value;

    // Auto-add new line if typing in the last one
    if (index === ideas.length - 1 && value.trim() !== '') {
      newIdeas.push('');
    }

    setIdeas(newIdeas);
  };

  const handleRemoveIdea = (index: number) => {
    const newIdeas = ideas.filter((_, i) => i !== index);
    if (newIdeas.length === 0) newIdeas.push('');
    setIdeas(newIdeas);
  };

  const handleSubmit = async () => {
    if (!seed || !userEmail || !startTime) return;

    const validIdeas = ideas.filter(idea => idea.trim() !== '');
    if (validIdeas.length === 0) return;

    setIsSubmitting(true);
    try {
      const endTime = new Date();
      const timeSpent = Math.round((endTime.getTime() - startTime.getTime()) / 1000);

      await ideasService.submitIdeas({
        seedId: seed.id,
        userEmail,
        content: validIdeas,
        timeSpent
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting ideas:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!seed) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white dark:bg-gray-900 dark:border-gray-800">
        <DialogHeader>
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg shrink-0">
              <Lightbulb className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-white leading-relaxed">
                {seed.prompt}
              </DialogTitle>
              <DialogDescription className="text-gray-500 dark:text-gray-400 mt-1">
                {seed.category} • Quick Spark Mode
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          {ideas.map((idea, index) => (
            <div key={index} className="flex gap-3 items-center group animate-in fade-in slide-in-from-bottom-2">
              <div className="w-8 h-8 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full text-gray-500 font-medium text-sm shrink-0">
                {index + 1}
              </div>
              <input
                type="text"
                value={idea}
                onChange={(e) => handleIdeaChange(index, e.target.value)}
                placeholder="Type your idea here..."
                className="flex-1 p-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all"
                autoFocus={index === 0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (idea.trim()) {
                      const newIdeas = [...ideas];
                      if (index === ideas.length - 1) {
                        newIdeas.push('');
                        setIdeas(newIdeas);
                      }
                      // Focus next input logic would go here if we had refs
                    } else if (index !== ideas.length - 1) {
                        // Focus next
                    } else {
                        handleSubmit();
                    }
                  }
                }}
              />
              {ideas.length > 1 && (
                <button
                  onClick={() => handleRemoveIdea(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t dark:border-gray-800">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || ideas.filter(i => i.trim()).length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Submit Ideas
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
